import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createLatlonCache, getLatlonCaches } from '@/services/admin/latlon-caches';
import type {
  LatlonCacheDetailResponse,
  LatlonCacheListParams,
  LatlonCacheStorePayload,
} from '@/services/admin/latlon-caches';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage } from '@/utils/apiError';

export type { LatlonCacheListParams } from '@/services/admin/latlon-caches';

export const useAdminLatlonCachesList = (site: string | null, params: LatlonCacheListParams) => {
  const toast = useAppToast();

  const query = useQuery({
    queryKey: ['admin', 'latlon-caches', site ?? '', params],
    queryFn: () => getLatlonCaches(site!, params),
    enabled: !!site,
    staleTime: 0,
  });

  useEffect(() => {
    if (query.isError && query.error) {
      toast.error(getApiErrorMessage(query.error, 'エラーが発生しました。'));
    }
  }, [query.isError, query.error, toast]);

  return query;
};

interface CreateLatlonCacheVariables {
  site: string;
  payload: LatlonCacheStorePayload;
}

export const useAdminLatlonCacheCreate = () => {
  const queryClient = useQueryClient();
  const toast = useAppToast();

  const { mutateAsync: createLatlonCacheMutation, isPending: isLoading } = useMutation<
    LatlonCacheDetailResponse,
    Error,
    CreateLatlonCacheVariables
  >({
    mutationFn: ({ site, payload }) => createLatlonCache(site, payload),
    onSuccess: response => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['admin', 'latlon-caches'] });
        toast.success('更新しました。');
      } else {
        toast.error(response.message ?? '登録に失敗しました。');
      }
    },
    onError: error => {
      // 422 validation errors (incl. duplicate lat/lng) are surfaced inline
      // by the page (setFieldErrors + generic toast there). Skip toast here
      // to avoid showing the backend message twice.
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 422) {
        return;
      }
      toast.error(getApiErrorMessage(error, 'エラーが発生しました。'));
    },
  });

  return { createLatlonCacheMutation, isLoading };
};
